<script setup lang="ts">
import type { RouteLocationRaw } from "vue-router";

import type { AlertRadius, AlertVariant } from "@/dsComponents/Alert/types";

import Link from "@/dsComponents/buttons/Link.vue";

interface AlertProps {
    message: string;
    icon?: string;
    variant?: AlertVariant;
    radius?: AlertRadius;
    linkLabel?: string;
    linkTo?: RouteLocationRaw;
}

withDefaults(defineProps<AlertProps>(), {
    variant: "neutral",
    radius: "xs",
});

const emit = defineEmits<{
    "link-click": [event: MouseEvent];
}>();
</script>

<template>
    <div :class="['alert-component', variant, `radius-${radius}`]">
        <i v-if="icon" :class="['alert-component__icon', `ti ti-${icon}`]" />
        <div class="alert-component__content">
            <span class="alert-component__message typo-xxs-regular">{{ message }}</span>
            <Link v-if="linkLabel" type="neutral" size="xs" noPadding underline :label="linkLabel" :to="linkTo" @click="emit('link-click', $event)" />
        </div>
    </div>
</template>

<style scoped lang="scss">
.alert-component {
    display: flex;
    align-items: flex-start;
    gap: var(--spacing-lg, 12px);
    padding: var(--spacing-md, 8px) var(--spacing-lg, 12px);
    border: 1px solid transparent;
    border-radius: var(--radius-xs, 4px);

    &.radius-sm {
        border-radius: var(--radius-sm, 6px);
    }

    &__icon {
        flex-shrink: 0;
        width: 16px;
        height: 18px;
        font-size: 16px;
        line-height: var(--font-xxs-regular-line-height, 18px);
    }

    &__content {
        display: flex;
        flex: 1;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-md, 8px);
        min-width: 0;
    }

    &__message {
        word-break: break-word;
        color: var(--text-main-900, #00061a);
    }

    &.neutral {
        border-color: var(--stroke-disabled-100, #eff2f5);
        background: var(--bg-lightest-50, #f6f7f9);

        .alert-component__icon {
            color: var(--icon-strong-900, #00061a);
        }
    }

    &.blue {
        border-color: var(--blue-lighter, #ebefff);
        background: var(--blue-lightest, #f5f7ff);

        .alert-component__icon {
            color: var(--blue-base, #2051f8);
        }
    }

    &.red,
    &.error {
        border-color: var(--red-lighter, #fdedf0);
        background: var(--red-lightest, #fef6f8);

        .alert-component__icon {
            color: var(--red-base, #e51940);
        }
    }

    &.error .alert-component__message {
        color: var(--red-base, #e51940);
    }

    &.green {
        border-color: var(--green-lighter, #ecfef5);
        background: var(--green-lightest, #f5fefa);

        .alert-component__icon {
            color: var(--green-base, #00a355);
        }
    }

    &.yellow {
        border-color: var(--yellow-lighter, #fef7e6);
        background: var(--yellow-lightest, #fffcf5);

        .alert-component__icon {
            color: var(--yellow-base, #fab305);
        }
    }

    &.purple {
        border-color: var(--purple-lighter, #f4edfd);
        background: var(--purple-lightest, #f9f6fe);

        .alert-component__icon {
            color: var(--purple-base, #7520e6);
        }
    }
}
</style>
